import type { QuarterForecast } from '@/lib/crm'

import { useState } from 'react'
import { formatCompactMoney, formatMoney } from '@/lib/companies'
import { monthLabel } from '@/lib/dates'

export const FORECAST_SERIES = [
    { key: 'won', label: "Closed won", color: '#9ec5f4' },
    { key: 'commit', label: "Commit", color: '#5598e7' },
    { key: 'bestCase', label: "Best case", color: '#256abf' },
    { key: 'pipeline', label: "Pipeline", color: '#184f95' }
] as const

const GRID_STEPS = 4

function niceMax(value: number): number {
    if (value <= 0) return 100000

    const rough = value / GRID_STEPS
    const magnitude = 10 ** Math.floor(Math.log10(rough))
    const step = [1, 2, 2.5, 5, 10].map(factor => factor * magnitude).find(candidate => candidate >= rough) ?? 10 * magnitude

    return step * GRID_STEPS
}

interface ForecastChartProps {
    forecast: QuarterForecast
}

export default function ForecastChart({ forecast }: ForecastChartProps) {
    const [active, setActive] = useState<string | null>(null)
    const totals = forecast.months.map(month => FORECAST_SERIES.reduce((sum, series) => sum + month.totals[series.key], 0))
    const max = niceMax(Math.max(...totals, 0))
    const ticks = Array.from({ length: GRID_STEPS + 1 }, (_, index) => (max / GRID_STEPS) * index)

    return (
        <figure className='flex flex-col gap-4'>
            <ul className='caption-style text-soft flex flex-wrap items-center gap-x-4 gap-y-2' aria-label="Legend">
                {FORECAST_SERIES.map(series => (
                    <li key={series.key} className='flex items-center gap-1.5'>
                        <span aria-hidden className='size-2.5 rounded-[3px]' style={{ backgroundColor: series.color }} />
                        {series.label}
                    </li>
                ))}
            </ul>

            <div className='relative flex h-[240px] gap-3 pt-6' aria-hidden>
                <div className='caption-style text-subtle relative w-12 shrink-0'>
                    {ticks.map(tick => (
                        <span
                            key={tick}
                            className='absolute right-0 -translate-y-1/2 tabular-nums'
                            style={{ bottom: `${(tick / max) * 100}%` }}
                        >
                            {formatCompactMoney(tick)}
                        </span>
                    ))}
                </div>

                <div className='relative flex-1'>
                    {ticks.map(tick => (
                        <span
                            key={tick}
                            className='absolute inset-x-0 h-px bg-white/6'
                            style={{ bottom: `${(tick / max) * 100}%` }}
                        />
                    ))}

                    <div className='absolute inset-0 grid grid-cols-3'>
                        {forecast.months.map((month, index) => {
                            const total = totals[index]
                            const isActive = active === month.month

                            return (
                                <div
                                    key={month.month}
                                    className='relative flex h-full items-end justify-center'
                                    onMouseEnter={() => setActive(month.month)}
                                    onMouseLeave={() => setActive(null)}
                                >
                                    <div className='relative w-6' style={{ height: `${(total / max) * 100}%` }}>
                                        <div className='flex h-full flex-col-reverse gap-[2px]'>
                                            {FORECAST_SERIES.map(series => {
                                                const value = month.totals[series.key]

                                                if (value <= 0) return null

                                                return (
                                                    <span
                                                        key={series.key}
                                                        className='block min-h-[2px] w-full last:rounded-t-[4px]'
                                                        style={{ flex: `${value} 0 0`, backgroundColor: series.color }}
                                                    />
                                                )
                                            })}
                                        </div>
                                        {total > 0 && (
                                            <span className='caption-style text-soft absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap tabular-nums'>
                                                {formatCompactMoney(total)}
                                            </span>
                                        )}
                                    </div>

                                    {isActive && (
                                        <div
                                            className='border-line-strong bg-popover shadow-overlay pointer-events-none absolute top-0 z-10 flex w-[200px] flex-col gap-2 rounded-lg border p-3'
                                            style={index < forecast.months.length - 1 ? { left: 'calc(50% + 20px)' } : { right: 'calc(50% + 20px)' }}
                                        >
                                            <span className='caption-style text-foreground font-medium'>
                                                {monthLabel(month.month)}
                                            </span>
                                            {FORECAST_SERIES.map(series => (
                                                <span key={series.key} className='caption-style flex items-center justify-between gap-3'>
                                                    <span className='text-soft flex items-center gap-1.5'>
                                                        <span className='size-2 rounded-[2px]' style={{ backgroundColor: series.color }} />
                                                        {series.label}
                                                    </span>
                                                    <span className='text-foreground tabular-nums'>${formatMoney(month.totals[series.key])}</span>
                                                </span>
                                            ))}
                                            <span className='caption-style border-line-strong flex items-center justify-between gap-3 border-t pt-2'>
                                                <span className='text-soft'>Weighted</span>
                                                <span className='text-foreground tabular-nums'>${formatMoney(month.totals.weighted)}</span>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            <div className='flex gap-3' aria-hidden>
                <span className='w-12 shrink-0' />
                <div className='caption-style text-soft grid flex-1 grid-cols-3 text-center'>
                    {forecast.months.map(month => (
                        <span key={month.month}>{monthLabel(month.month)}</span>
                    ))}
                </div>
            </div>

            <table className='sr-only'>
                <caption>Forecast by month</caption>
                <thead>
                    <tr>
                        <th scope='col'>Month</th>
                        {FORECAST_SERIES.map(series => (
                            <th key={series.key} scope='col'>{series.label}</th>
                        ))}
                        <th scope='col'>Weighted</th>
                    </tr>
                </thead>
                <tbody>
                    {forecast.months.map(month => (
                        <tr key={month.month}>
                            <th scope='row'>{monthLabel(month.month)}</th>
                            {FORECAST_SERIES.map(series => (
                                <td key={series.key}>${formatMoney(month.totals[series.key])}</td>
                            ))}
                            <td>${formatMoney(month.totals.weighted)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    )
}
